import React, { useState, useEffect, useCallback } from 'react';
import { ordersApi, Order } from '../../api/payments';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Search, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';
import { ViewDialog, DetailSection, DetailItem } from '../../components/shared/ViewDialog';

interface OrdersPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 10;

export default function OrdersPage({ isDark }: OrdersPageProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page: currentPage, limit: ITEMS_PER_PAGE };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await ordersApi.getAll(params);
      setOrders(response.items);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSearch = (value: string) => { setSearchQuery(value); setCurrentPage(1); };
  const handleStatusFilter = (value: string) => { setStatusFilter(value); setCurrentPage(1); };

  const handleView = async (order: Order) => {
    try {
      const fullOrder = await ordersApi.getById(order.id);
      setViewingOrder(fullOrder as Order);
      setIsViewDialogOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load order details');
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price: number, currency: string = 'UZS') => {
    return new Intl.NumberFormat('uz-UZ', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pending', className: 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/50 cursor-pointer' },
      paid: { label: 'Paid', className: 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/50 cursor-pointer' },
      cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-red-500 hover:bg-red-500/50 cursor-pointer' },
      refunded: { label: 'Refunded', className: 'bg-slate-500/10 text-slate-500 hover:bg-slate-500/50 cursor-pointer' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-slate-500/10 text-slate-500' };
    return <Badge className={cn('rounded-lg font-medium border-0', statusInfo.className)}>{statusInfo.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#0F172A]')}>Orders</h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>View and manage all orders</p>
      </div>

      <Card className={cn('rounded-2xl border-0 overflow-hidden', isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50')}>
        <div className={cn('p-5 lg:p-6 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>All Orders</h3>
              <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>{total} total orders</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-1 lg:flex-initial lg:max-w-md">
              <div className="relative flex-1">
                <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4', isDark ? 'text-slate-500' : 'text-slate-400')} />
                <Input type="text" placeholder="Search orders..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
                  className={cn('pl-10 h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white placeholder:text-slate-500' : 'bg-slate-100 text-slate-900 placeholder:text-slate-400')} />
              </div>
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className={cn('w-full sm:w-[140px] h-10 rounded-xl border-0 cursor-pointer', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')}>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="cursor-pointer">
                  <SelectItem value="all" className="cursor-pointer">All Statuses</SelectItem>
                  <SelectItem value="pending" className="cursor-pointer">Pending</SelectItem>
                  <SelectItem value="paid" className="cursor-pointer">Paid</SelectItem>
                  <SelectItem value="cancelled" className="cursor-pointer">Cancelled</SelectItem>
                  <SelectItem value="refunded" className="cursor-pointer">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Order ID</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>User</TableHead>
                  <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Plan</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Amount</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Status</TableHead>
                  <TableHead className={cn('font-semibold hidden sm:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Created</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">No orders found</TableCell></TableRow>
                ) : orders.map((order) => (
                  <TableRow key={order.id} className={cn('transition-colors', isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50')}>
                    <TableCell className={cn('font-mono text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{order.id.substring(0, 8)}...</TableCell>
                    <TableCell>
                      <div>
                        <p className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>{order.user?.username || 'Unknown'}</p>
                        {order.user?.email && <p className={cn('text-xs mt-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>{order.user.email}</p>}
                      </div>
                    </TableCell>
                    <TableCell className={cn('hidden md:table-cell', isDark ? 'text-slate-300' : 'text-slate-700')}>{order.plan?.title || order.slides_package?.name_uz || '—'}</TableCell>
                    <TableCell>
                      <div>
                        <p className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>{formatPrice(order.final_amount, order.currency)}</p>
                        {order.discount_amount > 0 && <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>Discount: {formatPrice(order.discount_amount, order.currency)}</p>}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell className={cn('hidden sm:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>{formatDate(order.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleView(order)} className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100')} title="View details">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {!isLoading && orders.length > 0 && (
          <Pagination isDark={isDark} currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        )}
      </Card>

      <ViewDialog isDark={isDark} isOpen={isViewDialogOpen} onOpenChange={setIsViewDialogOpen} title="Order Details" description="Complete order information">
        {viewingOrder && (
          <div className="space-y-6 py-4">
            <DetailSection title="Order Information" isDark={isDark}>
              <DetailItem label="Order ID" isDark={isDark} mono breakAll>{viewingOrder.id}</DetailItem>
              <DetailItem label="Status" isDark={isDark}>{getStatusBadge(viewingOrder.status)}</DetailItem>
              <DetailItem label="Plan" isDark={isDark}>{viewingOrder.plan?.title || viewingOrder.slides_package?.name_uz || '—'}</DetailItem>
              <DetailItem label="User" isDark={isDark}>{viewingOrder.user_id}</DetailItem>
            </DetailSection>
            <DetailSection title="Payment Information" isDark={isDark}>
              <DetailItem label="Original Amount" isDark={isDark}>{formatPrice(viewingOrder.original_amount, viewingOrder.currency)}</DetailItem>
              <DetailItem label="Discount" isDark={isDark}>{formatPrice(viewingOrder.discount_amount, viewingOrder.currency)}</DetailItem>
              <DetailItem label="Final Amount" isDark={isDark}><span className="text-lg font-bold">{formatPrice(viewingOrder.final_amount, viewingOrder.currency)}</span></DetailItem>
              {viewingOrder.promo_code && <DetailItem label="Promo Code" isDark={isDark}><Badge className="bg-purple-500/10 text-purple-500">{viewingOrder.promo_code.code}</Badge></DetailItem>}
            </DetailSection>
            <DetailSection title="Dates" isDark={isDark}>
              <DetailItem label="Created At" isDark={isDark}>{formatDate(viewingOrder.created_at)}</DetailItem>
              <DetailItem label="Paid At" isDark={isDark}>{formatDate(viewingOrder.paid_at)}</DetailItem>
            </DetailSection>
          </div>
        )}
      </ViewDialog>
    </div>
  );
}
